import type { FormField, FormSection } from '@/components/form-builder/types'

/**
 * Provenance repair for Process fields authored on the builder canvas.
 *
 * A Process field can reach the canvas two ways, and only one of them used to
 * work end to end:
 *
 *  - inserted from the Field Library, where `createFieldFromLibrary` stamps
 *    `libraryFieldId`; or
 *  - dropped from the palette's "Special" group, where `createField` gives it a
 *    fresh uuid and no provenance at all.
 *
 * The second kind is a dead end. `ProcessStepField` resolves its config through
 * `libraryFieldId ?? id`, and the Process page enumerates candidates from the
 * Field Library — so a palette-dropped Process field has nothing to resolve to
 * and never appears anywhere it could be configured. It renders as an
 * unconfigured process forever.
 *
 * The fix is to give it the same provenance the library path has: create a
 * library row for it at save time and stamp the id, so from then on it is an
 * ordinary library-backed Process field. These helpers are the pure half of
 * that; the save handler owns the API calls.
 */

/** True when this field is a Process field with no Field Library row behind it. */
function needsLibraryRow(f: FormField): boolean {
  return f.type === 'process' && !f.libraryFieldId
}

/**
 * Every Process field on the canvas that has no library row yet, in section
 * order. Fields inserted from the library are skipped: they already resolve
 * through their provenance, and minting a second row would fork the config the
 * form is meant to share.
 */
export function unregisteredProcessFields(sections: FormSection[]): FormField[] {
  return sections.flatMap(s => s.fields.filter(needsLibraryRow))
}

/**
 * Process fields about to be filed in the Field Library under a name that
 * identifies nothing (z8nrz7cr9r).
 *
 * A process dropped from the palette starts life labelled with the field TYPE —
 * "Process" — and the save handler files it in the library under whatever its
 * label says. Leave the default in place across three forms and the library ends
 * up holding three rows called "Process", indistinguishable in every picker that
 * lists them. That is exactly the data this codebase already had.
 *
 * It matters more than an untidy name, because config is keyed on the LIBRARY
 * field: picking the wrong "Process" silently edits another form's bill of
 * materials. The picker can qualify the rows after the fact, but it cannot
 * recover the intent nobody recorded.
 *
 * So this is refused rather than papered over. Deriving a name here — appending
 * the form name, or a counter — would invent authorship the app does not have,
 * and would repeat the mistake the Process page's removed "create a process"
 * affordance made: manufacturing library rows nobody meaningfully named. The
 * author is one rename away from the right answer, and only they know it.
 *
 * `defaultLabel` is passed in rather than imported so this stays a pure module
 * with no dependency on the builder's component config.
 */
export function unnamedProcessFields(
  sections: FormSection[],
  defaultLabel: string,
): FormField[] {
  const placeholder = defaultLabel.trim().toLowerCase()
  return unregisteredProcessFields(sections).filter(f => {
    const label = f.label.trim().toLowerCase()
    // Blank counts too: the save handler used to fall back to the same default,
    // so an empty label reached the library as "Process" just the same.
    return label === '' || label === placeholder
  })
}

/**
 * Names shared by two DIFFERENT processes on one canvas (z8nrz7cr9r).
 *
 * The API now refuses a Process whose name another Process already holds, and
 * the save registers pending fields with `Promise.all` — so one rejection after
 * a sibling has already been created leaves an orphan library row behind. The
 * most reachable way to cause that is two same-named processes on the same form,
 * which this catches before any request is made.
 *
 * Compared by RESOLVED id (`libraryFieldId ?? id`), not by position, because a
 * form embedding one process twice is legitimate — both copies point at the same
 * library row, share its configuration, and are not a conflict. Two copies
 * resolving to different rows under one name are.
 *
 * Blank and default-named fields are left to `unnamedProcessFields`, which
 * reports them with better advice than "these two clash".
 *
 * @returns the offending labels as authored, for use in a message.
 */
export function conflictingProcessNames(sections: FormSection[]): string[] {
  const seen = new Map<string, { label: string; ids: Set<string> }>()

  for (const section of sections) {
    for (const f of section.fields) {
      if (f.type !== 'process') continue
      const label = f.label.trim()
      if (label === '') continue

      const key = label.toLowerCase()
      const resolved = f.libraryFieldId ?? f.id
      const held = seen.get(key)
      if (held) held.ids.add(resolved)
      else seen.set(key, { label, ids: new Set([resolved]) })
    }
  }

  return [...seen.values()].filter(e => e.ids.size > 1).map(e => e.label)
}

/**
 * The id each Process field's configuration is keyed by — its provenance when
 * it came from the library, otherwise its own id. This is the same resolution
 * `ProcessStepField` performs (`libraryFieldId ?? id`), so the set produced here
 * is exactly the set of processes the form currently uses.
 */
export function processFieldIds(sections: FormSection[]): Set<string> {
  return new Set(
    sections.flatMap(s =>
      s.fields.filter(f => f.type === 'process').map(f => f.libraryFieldId ?? f.id),
    ),
  )
}

/**
 * Which processes the form used to carry and no longer does — the ones a save
 * would orphan.
 *
 * Compares by resolved id rather than by position, so moving a field between
 * sections or reordering the canvas is not mistaken for a removal.
 */
export function removedProcessFieldIds(
  before: Set<string>,
  after: FormSection[],
): string[] {
  const surviving = processFieldIds(after)
  return [...before].filter(id => !surviving.has(id))
}

/**
 * Stamp `libraryFieldId` onto the fields named in `byFieldId`, leaving every
 * other field and property untouched.
 *
 * Returns the original array when there is nothing to stamp, so the caller can
 * skip a state update — and with it a re-render — on the common save where no
 * Process field was added.
 */
export function stampLibraryFieldIds(
  sections: FormSection[],
  byFieldId: Map<string, string>,
): FormSection[] {
  if (byFieldId.size === 0) return sections
  return sections.map(s => ({
    ...s,
    fields: s.fields.map(f => {
      const libraryFieldId = byFieldId.get(f.id)
      return libraryFieldId ? { ...f, libraryFieldId } : f
    }),
  }))
}
